import React, { useState, useEffect } from 'react';
import { X, AlertCircle, CheckCircle2, Calendar } from 'lucide-react';
import close from '../../assets/design/pricing/closing-x.png';
import Spinner from '../../components/layout/Spinner';
import FadeIn from '../../utils/fadein';
import {declineDemoBuyer, addBuyerDeclineReason, declineDemoSeller, addSellerDeclineReason, getBuyerDeclinedReasons} from '../../utils/api';
import { getCurrentUser, refresh } from '../../services/auth.service';

const DeclinedPopup = ({ sellerDeclinedReasons, setShowDeclinedPopup, role, currentDemo, demos, setDemos, source, setShowReschedule}) => {
  const [buyerDeclinedReasons, setBuyerDeclinedReasons] = useState([]);
  const [declineReason, setDeclineReason] = useState('');
  const [selectedDeclineReason, setSelectedDeclineReason] = useState(null);
  const [showAreYouSure, setShowAreYouSure] = useState(true);
  const [showNotification, setShowNotification] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  async function fetchBuyerDeclinedReasons() {
    let data = await getBuyerDeclinedReasons();
    if (data.success) {
      setBuyerDeclinedReasons(data.data);
    }
  }

  useEffect(() => {
    fetchBuyerDeclinedReasons();
  }, []);

  function closingClick() {
    setShowDeclinedPopup(false);
  }

  function notificationClosingClick() {
    setShowNotification(false);
    setShowDeclinedPopup(false);
  }

  async function handleSubmit(event, id) {
    event.preventDefault();

    if (selectedDeclineReason === null){
      alert("Please select a reason");
      return;
    }

    setIsProcessing(true);

    if (role === 'Buyer') {
      let res = await declineDemoBuyer(id, source, declineReason);

      if (res.success){
        await addBuyerDeclineReason(id, selectedDeclineReason, declineReason);
        setShowAreYouSure(!showAreYouSure);
        setShowNotification(true);
        currentDemo.demo_status = 'Declined';
        currentDemo.buyer_status = 'Declined';
      }

      else{
        await refresh();

        let res = await declineDemoBuyer(id, source, declineReason);

        if (res.success){
          await addBuyerDeclineReason(id, selectedDeclineReason, declineReason);
          setShowAreYouSure(!showAreYouSure);
          setShowNotification(true);
          currentDemo.demo_status = 'Declined';
          currentDemo.buyer_status = 'Declined';
        }
      }
    }
    
    else {
      let res = await declineDemoSeller(id, source);

      if (res.success){
        await addSellerDeclineReason(id, selectedDeclineReason, declineReason);
        setShowAreYouSure(!showAreYouSure);
        setShowNotification(true);
        currentDemo.demo_status = 'Declined';
        currentDemo.seller_status = 'Declined';
      }

      else if (res.status === 403){
        await refresh();

        let res = await declineDemoSeller(id, source);

        if (res.success){
          await addSellerDeclineReason(id, selectedDeclineReason, declineReason);
          setShowAreYouSure(!showAreYouSure);
          setShowNotification(true);
          currentDemo.demo_status = 'Declined';
          currentDemo.seller_status = 'Declined';
        }
      }
    }

    let _filtered = demos.filter(tmp => tmp.demo_id !== currentDemo.demo_id);
    setDemos(_filtered);
  }

  async function handleRescheduleClick(){
    setShowReschedule(true);
    setShowDeclinedPopup(false);
  }

  return (
    <>
      <div className='fixed inset-0 bg-black/50 backdrop-blur-sm'></div>
      {showAreYouSure && (
        <div className='fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full max-w-lg bg-white rounded-xl shadow-xl p-6'>
          <button 
            className='absolute top-4 right-4 p-1 rounded-lg hover:bg-gray-100 transition-colors' 
            onClick={closingClick}
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>

          {role === 'Buyer' && (
            <div className="flex items-start space-x-3 mb-6">
              <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" />
              <h1 className='text-xl font-semibold text-gray-900'>
                Are you sure you want to decline the demo with <strong>{currentDemo.company_name}</strong>?
              </h1>
            </div>
          )}

          {role === 'Seller' && source === 'Cancel' && (
            <div className="flex items-start space-x-3 mb-6">
              <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" />
              <h2 className='text-xl font-semibold text-gray-900'>
                Cancel this demo
              </h2>
            </div>
          )}

          {role === 'Buyer' && source === 'Cancel' && (
            <div className="flex items-center space-x-3 bg-blue-50 p-4 rounded-lg mb-6">
              <Calendar className="w-5 h-5 text-blue-500" />
              <p className="text-sm text-blue-700">
                Are you looking to reschedule instead? 
                <button 
                  className='ml-2 inline-flex items-center px-3 py-1 rounded-md bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors' 
                  onClick={() => handleRescheduleClick()}
                >
                  Reschedule
                </button>
              </p>
            </div>
          )}
          
          <form onSubmit={(e) => handleSubmit(e, currentDemo.demo_id)} className="space-y-6">
            <div className='form-group'>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Choose the reason
              </label>
              <select
                className='w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white shadow-sm'
                onChange={(e) => setSelectedDeclineReason(e.target.value)}
                name='choose reason'>
                <option value='0' className='text-gray-500'>Select a reason</option>
                {role === 'Buyer' && (
                  <>
                    {buyerDeclinedReasons.length > 0 &&
                      buyerDeclinedReasons.map((item) => (
                        <option 
                          value={item.decline_reason_id}
                          className='py-2'
                        >
                          {item.reason}
                        </option>
                      ))}
                  </>
                )}
                {role === 'Seller' && (
                  <>
                    {sellerDeclinedReasons.length > 0 &&
                      sellerDeclinedReasons.map((item) => (
                        <option 
                          value={item.decline_reason_id}
                          className='py-2'
                        >
                          {item.reason}
                        </option>
                      ))}
                  </>
                )}
              </select>
            </div>

            <div className='form-group mt-6'>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                If other reason, please briefly explain why:
              </label>

              <p className='text-xs text-gray-500 italic mb-2'>
                * will not be shared with other person
              </p>
              
              <textarea
                autoComplete='off'
                value={declineReason}
                onChange={(event) => setDeclineReason(event.target.value)}
                className='w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 min-h-[120px] resize-none'
                name='Objection Reason'
                placeholder='Enter your reason here...'
              />
            </div>

            <div className='flex justify-center'>
              {isProcessing ? (
                <Spinner />
              ) : (
                <button className={`
                  inline-flex items-center px-4 py-2 rounded-lg
                  ${source === 'Cancel' 
                    ? 'bg-red-600 hover:bg-red-700 text-white' 
                    : 'bg-gray-900 hover:bg-gray-800 text-white'
                  } transition-colors
                `}>
                  {source === 'Cancel' ? 'Confirm Cancel' : 'Not a fit'}
                </button>
              )}
            </div>
          </form>
        </div>
      )}

      {showNotification && (
        <>
          <div className='fixed inset-0 bg-black/50 backdrop-blur-sm'></div>
          <div className='fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-white rounded-xl shadow-xl p-6'>
            <button 
              className='absolute top-4 right-4 p-1 rounded-lg hover:bg-gray-100 transition-colors'
              onClick={notificationClosingClick}
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>

            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              </div>
              
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Demo Declined
              </h2>
              
              {role === 'Seller' && (
                <p className="text-gray-600 mb-6">
                  You have declined a demo with <span className="font-medium text-gray-900">{currentDemo.company_name}</span>. 
                  We will use this information to improve your future matches.
                </p>
              )}

              {role === 'Buyer' && (
                <p className="text-gray-600 mb-6">
                  You have declined the demo with <span className="font-medium text-gray-900">{currentDemo.company_name}</span>.
                  {source === 'Cancel' && (
                    <span className="block mt-2">
                      We have informed them of your cancellation. Please check back often for new demo opportunities!
                    </span>
                  )}
                </p>
              )}

              <button 
                onClick={notificationClosingClick}
                className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default DeclinedPopup;